'use client';

import { type FC, useCallback, useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Button } from '../../ui';
import { Credits } from '../../Credits';
import { FaCopy, FaGift, FaShare, IoPricetag } from '../../icons';
import { SideIcon } from '../SideIcon';
import { sideColor } from './sideColor';
import { DropReveal, type RevealDrop } from './DropReveal';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useRushRaffleMatchAward } from '../../../queries/useRushRaffleQueries';
import translations from './RushMid.translations';
import type { Payout } from './economy';
import type { ScoreEntry } from './useRushMidMatch';

const DropThumb: FC<{ image: string | null; name: string; size: number }> = ({ image, name, size }) =>
  image ? (
    <img src={image} alt={name} className="shrink-0 object-contain" style={{ width: size, height: size }} />
  ) : (
    <span
      className="flex shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold"
      style={{ width: size, height: size }}
    >
      <FaGift size={Math.round(size * 0.55)} />
    </span>
  );

/** The result body shared by the post-match screen and the history detail:
 *  winning-side header + kills score + CS-style scoreboard (winner team first,
 *  split from the losers by a thick divider). No buttons / screen wrapper. */
export const ResultBoard: FC<{
  payout: Payout;
  scoreboard?: ScoreEntry[];
  youFighterId?: string | null;
  /** the match id — shown with a copy button so the player can quote it to support. */
  matchId?: string;
  /** share this match's replay deep link (absent → no share button rendered). */
  onShare?: () => void;
  sharing?: boolean;
  /** play the case-open reveal sequence — live result only, not history re-opens. */
  celebrateDrops?: boolean;
}> = ({ payout, scoreboard, youFighterId, matchId, onShare, sharing, celebrateDrops = false }) => {
  const { t } = useLocalTranslation(translations);
  const { won, draw, pot } = payout;
  const award = useRushRaffleMatchAward(pot > 0 ? matchId ?? null : null).data;
  const ticketChips: { key: string; label: string }[] = [];
  if (award) {
    const b = award.breakdown;
    if (b.base > 0) ticketChips.push({ key: 'win', label: t('raffleChipWin', { n: b.base }) });
    if (b.mvp > 0) ticketChips.push({ key: 'mvp', label: t('raffleChipMvp', { n: b.mvp }) });
    if (b.multiKill === 1) ticketChips.push({ key: 'triple', label: t('raffleChipTriple', { n: b.multiKill }) });
    if (b.multiKill === 2) ticketChips.push({ key: 'quad', label: t('raffleChipQuad', { n: b.multiKill }) });
    if (b.multiKill === 4) ticketChips.push({ key: 'ace', label: t('raffleChipAce', { n: b.multiKill }) });
    if (b.oneHp > 0) ticketChips.push({ key: 'oneHp', label: t('raffleChipOneHp', { n: b.oneHp }) });
    if (b.clutch === 2) ticketChips.push({ key: 'clutch2', label: t('raffleChipClutch2', { n: b.clutch }) });
    if (b.clutch === 3) ticketChips.push({ key: 'clutch3', label: t('raffleChipClutch3', { n: b.clutch }) });
  }

  const yourTeam: 't' | 'ct' = youFighterId?.startsWith('ct') ? 'ct' : 't';
  // draw = no winner; keep your side first in the board. Otherwise winner's side first.
  const winTeam: 't' | 'ct' = won || draw ? yourTeam : yourTeam === 't' ? 'ct' : 't';

  const [copied, setCopied] = useState(false);
  const copyId = useCallback(() => {
    if (!matchId) return;
    void navigator.clipboard.writeText(matchId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }).catch(() => undefined);
  }, [matchId]);

  const kills = (team: 't' | 'ct') =>
    (scoreboard ?? []).filter((s) => s.team === team).reduce((a, s) => a + s.kills, 0);

  const rows = scoreboard ?? [];
  const ordered = [
    ...rows.filter((s) => s.team === winTeam),
    ...rows.filter((s) => s.team !== winTeam),
  ];
  const splitAt = rows.filter((s) => s.team === winTeam).length;

  const yourDrop = rows.find((s) => s.fighterId === youFighterId)?.drop;

  const revealDrops = useMemo<RevealDrop[]>(
    () =>
      (scoreboard ?? [])
        .filter((s): s is ScoreEntry & { drop: NonNullable<ScoreEntry['drop']> } => s.drop !== undefined)
        .map((s) => ({ fighterId: s.fighterId, name: s.name, team: s.team, drop: s.drop })),
    [scoreboard],
  );
  const [revealDone, setRevealDone] = useState(false);
  const markRevealDone = useCallback(() => setRevealDone(true), []);

  if (celebrateDrops && revealDrops.length > 0 && !revealDone) {
    return (
      <div className="flex min-h-full flex-col px-4 pt-3 pb-28">
        <DropReveal drops={revealDrops} onDone={markRevealDone} />
      </div>
    );
  }

  return (
    <div className="flex flex-col px-4 pt-3 pb-28">
      {/* header: winning side (or DRAW) + kills score */}
      <div className="flex items-center gap-3">
        {draw ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-3xl font-black text-label-secondary">=</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-label-secondary">{t('drawRefund')}</div>
              <div className="text-2xl font-extrabold text-label-primary">{t('matchDraw')}</div>
            </div>
          </>
        ) : (
          <>
            <SideIcon side={winTeam} size={64} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-label-secondary">{t('wonShort')}</div>
              <div className={cn('text-2xl font-extrabold', sideColor(winTeam, 'text'))}>
                {t(winTeam === 'ct' ? 'sideCt' : 'sideT')}
              </div>
            </div>
          </>
        )}
        <div className="text-3xl font-extrabold tabular-nums">
          <span className="text-gold">{kills('t')}</span>
          <span className="text-label-secondary"> : </span>
          <span className="text-accent-blue">{kills('ct')}</span>
        </div>
      </div>

      {/* personal skin drop — celebratory panel for the current player */}
      {yourDrop && (
        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3">
          <DropThumb image={yourDrop.image} name={yourDrop.name} size={56} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold uppercase tracking-wide text-gold">{t('dropYouTitle')}</div>
            <div className="truncate text-base font-extrabold text-label-primary">{yourDrop.name}</div>
          </div>
          <span className="shrink-0 rounded-lg bg-gold/20 px-2 py-1 text-sm font-bold text-gold">
            <Credits amount={yourDrop.priceCredits} iconSize={14} />
          </span>
        </div>
      )}

      {/* raffle tickets earned this match — absent on practice / no award */}
      {award && award.tickets > 0 && (
        <div className="mt-5 rounded-2xl bg-bg-secondary px-3 py-3">
          <div className="flex items-center gap-2">
            <IoPricetag className="shrink-0 text-lg text-gold" />
            <span className="text-sm font-extrabold text-gold">{t('raffleTicketsTitle', { n: award.tickets })}</span>
          </div>
          {ticketChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ticketChips.map((chip) => (
                <span key={chip.key} className="rounded-full bg-gold/15 px-2 py-1 text-[11px] font-semibold text-gold">
                  {chip.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CS-style scoreboard — kills / points (damage+utility) / winnings per fighter */}
      {scoreboard && scoreboard.length > 0 && (
        <div className="mt-5 w-full overflow-hidden rounded-2xl bg-bg-secondary">
          <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-label-secondary">
            <span className="flex-1 text-left">{t('scoreboardTitle')}</span>
            <span className="w-8 text-center">{t('colKills')}</span>
            <span className="w-12 text-center">PTS</span>
            <span className="w-12 text-center">ELO</span>
            <span className="w-20 text-right">{t('payout')}</span>
          </div>
          {ordered.map((s, i) => {
            const you = s.fighterId === youFighterId;
            return (
              <div
                key={s.fighterId}
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 text-sm',
                  i === 0 ? '' : i === splitAt ? 'border-t-4 border-white/20' : 'border-t border-white/5',
                  you && 'bg-white/5',
                  (!s.alive || s.afk) && 'opacity-55',
                )}
              >
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', s.team === 't' ? 'bg-accent-red' : 'bg-accent-blue')} />
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-left font-semibold text-label-primary">
                  <span className="truncate">{s.name}</span>
                  {s.mvp && (
                    <span className="shrink-0 rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-gold">MVP</span>
                  )}
                  {s.afk && (
                    <span className="shrink-0 rounded bg-accent-red/20 px-1.5 py-0.5 text-[10px] font-bold text-accent-red">{t('afkBadge')}</span>
                  )}
                </span>
                <span className="w-8 text-center font-bold tabular-nums text-label-primary">{s.kills}</span>
                <span className="w-12 text-center font-bold tabular-nums text-label-secondary">{s.pts}</span>
                <span className="w-12 text-center font-bold tabular-nums">
                  {/* undefined for an unrated/bot seat → «—»; an AFK winner carries a real
                      negative delta (doubled loss) and renders red like any loss. */}
                  {s.ratingDelta === undefined
                    ? <span className="text-label-secondary">—</span>
                    : <span className={s.ratingDelta >= 0 ? 'text-accent-green' : 'text-accent-red'}>{s.ratingDelta >= 0 ? '+' : ''}{s.ratingDelta}</span>}
                </span>
                <span className="w-20 text-right tabular-nums">
                  {s.drop
                    // a winner is paid the skin, never coins — show the item, not a coin amount
                    ? (
                      <span className="inline-flex items-center justify-end gap-1 font-bold text-gold">
                        {s.drop.image && <img src={s.drop.image} alt="" className="h-4 w-4 shrink-0 object-contain" />}
                        <span className="tabular-nums">{s.drop.priceCredits}</span>
                      </span>
                    )
                    // an AFK winner lost their ticket — show the −entry loss (net), not «—»
                    : s.payout !== 0
                      ? (
                        <span className={cn('font-bold', s.payout > 0 ? 'text-accent-green' : 'text-accent-red')}>
                          {s.payout > 0 ? '+' : '−'}<Credits amount={Math.abs(s.payout)} iconSize={12} compact />
                        </span>
                      )
                      : <span className="text-label-secondary">—</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* share the match replay — a deep link any signed-in user can open to watch it */}
      {onShare && matchId && (
        <Button
          variant="share"
          size="m"
          loading={sharing}
          onClick={onShare}
          before={<FaShare size={14} />}
          className="mt-5 self-start"
        >
          {t('shareReplay')}
        </Button>
      )}

      {/* match id + copy — so the player can quote it to support if something looks off */}
      {matchId && (
        <button
          type="button"
          onClick={copyId}
          className="mt-5 inline-flex items-center gap-2 self-start rounded-lg bg-bg-secondary px-3 py-2 text-xs font-semibold text-label-secondary active:opacity-70"
        >
          <FaCopy size={12} />
          {copied ? t('matchIdCopied') : t('copyMatchId')}
          <span className="font-mono text-label-tertiary">{matchId.slice(0, 8)}…</span>
        </button>
      )}
    </div>
  );
};
